import { Access } from "@p0security/shared/types/access/index";

import { DatabricksAiLevels } from "./levels.ts";

/**
 * The one access. It grants to a Databricks service principal that P0
 * creates for the request alone, never to a person's own Databricks user.
 */
export const DatabricksAiAccesses = {
  data: Access({
    delegation: {},
    description:
      "Read or write access to a Unity Catalog catalog, schema, table or volume, queried on one SQL warehouse, granted to an agent's own short-lived Databricks service principal",
    // The request details page lists these in this order, so keep it.
    generated: {
      // `p0-agent-<request ID>`, so every request has its own principal, and
      // revoking deletes it.
      principalName: {
        description: "The service principal P0 created for this request",
        label: "Service principal",
        type: "string",
      },
      applicationId: {
        description:
          "The application ID of the service principal P0 created, which the agent's MCP servers authenticate as",
        label: "Application ID",
        type: "string",
      },
      // Revoke goes back through the account and the workspace recorded here,
      // not whatever the account item holds by then. It looks the workspace up
      // again, so it never reads the staged host.
      accountId: {
        description: "The Databricks account P0 granted in",
        label: "Account ID",
        type: "string",
      },
      workspaceId: {
        description: "The workspace P0 granted through",
        label: "Workspace ID",
        type: "string",
      },
      workspaceHost: {
        description:
          "The https:// origin of the workspace P0 granted through, which the requesting MCP server covers",
        label: "Workspace host",
        type: "string",
      },
      addedGrants: {
        description:
          "The grants P0 added for this request, including the parent USE CATALOG and USE SCHEMA. Revoking removes only these.",
        label: "Added grants",
        multivalued: true,
        type: "string",
      },
    },
    label: "Data",
    maturity: "beta",
    // Required parameters are CLI positionals, so this reads
    // `data <workspace> <warehouse> <catalog> <level> [--schema] [--table | --volume]`.
    // The deepest securable given is the one granted on.
    parameters: {
      workspace: {
        description: "A workspace in an installed Databricks account, by ID",
        label: "Workspace",
        type: "dynamic",
      },
      warehouse: {
        description:
          "A pro or serverless SQL warehouse in the workspace, which the agent's queries run on",
        label: "Warehouse",
        type: "dynamic",
      },
      catalog: {
        description: "A Unity Catalog catalog the workspace can use",
        label: "Catalog",
        type: "dynamic",
      },
      level: {
        choices: DatabricksAiLevels,
        description:
          "read, or write, which also lets the agent change data. On a catalog or schema, write also lets it create tables, views, materialized views, volumes and functions.",
        label: "Level",
        type: "static",
      },
      schema: {
        description: "A schema in the catalog",
        label: "Schema",
        optional: true,
        type: "dynamic",
      },
      table: {
        description: "A table, view or materialized view in the schema",
        label: "Table",
        optional: true,
        type: "dynamic",
      },
      volume: {
        description: "A volume in the schema",
        label: "Volume",
        optional: true,
        type: "dynamic",
      },
      // The three below are set from the parameters above by the lifecycle's
      // `normalize`, because approval rules see only parameters. The
      // `securable` one also makes the approval message say what the level is
      // granted on: read on one table and read on a whole catalog would
      // otherwise read the same.
      securable: {
        description:
          "What the level is granted on, e.g. table main.sales.orders",
        label: "Securable",
        type: "generated",
      },
      account: {
        description: "The Databricks account the workspace is in",
        label: "Account",
        type: "generated",
      },
      workspaceName: {
        description:
          "The workspace's name in Databricks, which a workspace admin can change",
        label: "Workspace name",
        type: "generated",
      },
    },
    render: {
      locator: "workspace",
      policy: "level",
      resource: "securable",
    } as const,
  }),
};

export type DatabricksAiAccesses = typeof DatabricksAiAccesses;
